import React, { useContext, useMemo, useRef } from "react"
import { Controller, get, useFormContext, useFormState } from "react-hook-form"

import classNames from "classnames"

import { useAutoFocus } from "shared/lib/useAutoFocus"
import { useEvent } from "shared/lib/useEvent"
import { useMergedRefs } from "shared/lib/useMergedRefs"
import { useRovingTabIndex } from "shared/lib/useRovingTabIndex"
import { Direction } from "shared/types/Direction"

import { Input as DefaultInput, InputContext, InputRootProps } from "../Input"
import {
  ColorInputContext,
  ColorInputContextType
} from "./context/ColorInputContext"
import "./styles/_color-input.scss"
import { ColorInputButton } from "./ui/ColorInputButton"
import { ReadonlyColor } from "./ui/ReadonlyColor"

export interface ColorInputRootProps
  extends InputRootProps,
    ColorInputContextType {}

export interface ColorInputProps
  extends Omit<React.HTMLProps<HTMLInputElement>, "onChange"> {
  onChange?: (color: string) => void
  value?: string
}

const Root = React.memo(
  React.forwardRef(
    (
      { colors, className, ...props }: ColorInputRootProps,
      ref?: React.ForwardedRef<HTMLDivElement>
    ) => {
      const context = useMemo(() => ({ colors }), [colors])
      return (
        <ColorInputContext.Provider value={context}>
          <DefaultInput.Root
            {...props}
            className={classNames("color-input", className)}
            ref={ref}
          />
        </ColorInputContext.Provider>
      )
    }
  )
)

Root.displayName = "ColorInput.Root"

const InputBase = React.memo(
  React.forwardRef(
    (
      { onChange, value, ...rest }: ColorInputProps,
      externalRef: React.ForwardedRef<HTMLInputElement>
    ) => {
      const internalRef = useRef<HTMLInputElement>(null)
      const { inputRef } = useContext(InputContext)
      const { id, autoFocus, label, disabled } = useContext(InputContext)
      const { colors } = useContext(ColorInputContext)

      useAutoFocus({ autoFocus, ref: internalRef })

      const ref = useMergedRefs(
        externalRef,
        internalRef,
        inputRef as React.Ref<HTMLInputElement>
      )

      const handleColorChange = useEvent((color: string) => {
        onChange?.(color)
      })

      const { containerProps, getElementProps } =
        useRovingTabIndex<HTMLButtonElement>({
          direction: Direction.HORIZONTAL,
          elementCount: colors.length,
          isRoundRobin: true
        })

      return (
        <>
          <input
            id={id}
            type="hidden"
            aria-label={label}
            disabled={disabled}
            {...rest}
            value={value}
            ref={ref}
          />
          <div
            role="group"
            className="color-input__picker"
            data-testid="color-input-picker"
            {...containerProps}
          >
            {colors.map((color, index) => (
              <ColorInputButton
                key={color.value}
                color={color}
                onColorChange={handleColorChange}
                isSelected={value === color.value}
                disabled={disabled}
                {...getElementProps(index)}
                autoFocus={index === 0 && autoFocus}
              />
            ))}
          </div>
        </>
      )
    }
  )
)

InputBase.displayName = "ColorInput.InputBase"

const HookFormInput = React.memo(
  React.forwardRef(
    (
      { className, ...rest }: ColorInputProps,
      externalRef: React.ForwardedRef<HTMLInputElement>
    ) => {
      const { name, required } = useContext(InputContext)
      const { control } = useFormContext()
      const { errors } = useFormState({ name })
      const error = get(errors, name)

      return (
        <Controller
          name={name}
          control={control}
          rules={{ required }}
          render={({ field }) => (
            <InputBase
              {...field}
              {...rest}
              className={classNames({ error: !!error }, className) || undefined}
              onChange={field.onChange}
              ref={externalRef}
            />
          )}
        />
      )
    }
  )
)

HookFormInput.displayName = "ColorInput.HookFormInput"

const Input = React.memo(
  React.forwardRef(
    (props: ColorInputProps, externalRef: React.Ref<HTMLInputElement>) => {
      const { name } = useContext(InputContext)

      if (name) {
        return <HookFormInput {...props} ref={externalRef} />
      }
      return <InputBase {...props} ref={externalRef} />
    }
  )
)

Input.displayName = "ColorInput.Input"

export const ColorInput = Object.assign(Root, {
  ...DefaultInput,
  Input,
  Readonly: ReadonlyColor
})
